import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { Lang } from "@yatayat/core/money";

import type { staffMessages } from "~/lib/i18n-staff";

// Nepali strings await native-speaker review: docs/discovery/nepali-review.md.
// The operator app's and the console's strings are in i18n-staff, loaded with those pages.
const messages = bothLanguages({
  en: {
    appName: "Public Transport Services",
    // The header carries both names whatever the language, so these match in en and ne.
    siteNameNe: "सार्वजनिक यातायात सेवा",
    siteNameEn: "Public Transport Services",
    proofOfConcept: "Proof of concept",
    systemName: "Integrated Public Transport Information System (IPTIS), proposed name",
    footerLabel: "Site links",
    footerPassengers: "Passengers",
    footerOperators: "Operators",
    footerGovernment: "Government",
    footerPartners: "Partners and developers",
    footerDemoWallet: "Demo Wallet",
    footerNotice: "Proof of concept · demo data · not a government service",
    partnerApi: "Partner API",
    tagline: "Official fares, reliable schedules, one place.",
    demoData: "DEMO DATA",
    switchLanguage: "नेपाली",
    fareCheck: "Check the official fare",
    fareCheckBody: "See the legal fare for any route before you pay.",
    bookSeat: "Book a bus ticket",
    bookSeatBody: "Long-route buses with the official fare, from any channel.",
    verifyVehicle: "Check a vehicle",
    verifyVehicleBody: "Scan the sticker to check the permit, insurance and fare.",
    apiConnected: "Connected to the backbone",
    apiPlaces: "{n} places",
    apiDown: "Backbone unreachable",
    notFound: "Page not found",
    dateAd: "AD {date}",
    open: "Open",
    fareCheckIntro: "The legal fare for a bus journey, from DoTM's fare table in force today.",
    openData: "Open data",
    openDataLink: "Open data: routes, stops, timetables and fares, free to reuse",
    openDataIntro: "Routes, stops, timetables and official fares, free to download and reuse. No personal data is published.",
    openDataGtfs: "GTFS feed",
    openDataGtfsBody: "The standard transit format that journey planners and map apps read. Times are Nepal time; times after departure are estimates.",
    openDataRoutes: "Routes",
    openDataRoutesBody: "Every permitted route with its stops in order and its length.",
    openDataStops: "Stops",
    openDataStopsBody: "Bus parks, stops and towns with their location.",
    openDataSchedules: "Timetables",
    openDataSchedulesBody: "Each operator's regular departures by route, class and weekday.",
    openDataFares: "Official fares",
    openDataFaresBody: "The legal fare between any two stops of a route, for each bus class, under the fare table in force.",
    openDataDownload: "Download",
    openDataLicence: "Free to reuse with credit to Public Transport Services. Stop locations are approximate; operators and timetables are demo data.",
    installApp: "Install the app",
    installLink: "Install it on your phone to open it like an app",
    from: "From",
    to: "To",
    choosePlace: "Choose a place",
    busClass: "Bus class",
    classOrdinary: "Ordinary",
    classDeluxe: "Deluxe",
    classSuperDeluxe: "Super deluxe",
    electricBus: "Electric bus",
    samePlace: "Choose two different places.",
    noRoute: "No route runs between these places.",
    noFareTable: "No fare table is in force for this route.",
    somethingWrong: "Something went wrong. Please try again.",
    officialFare: "Official fare",
    distanceKm: "{km} km",
    fareTable: "Fare table: {label}, in force from {date}",
    howWorkedOut: "How this fare is worked out",
    howWorkedOutNote: "Each stretch is charged at its road's rate per km; the bus class multiplier applies to the total, rounded to the nearest rupee.",
    stretch: "Stretch",
    road: "Road",
    km: "km",
    perKm: "Per km",
    roadTerai: "Terai",
    roadHill: "Hill",
    roadKtmPokhara: "Kathmandu–Pokhara section",
    mainNav: "Main",
    navBook: "Book",
    navTickets: "My tickets",
    signOut: "Sign out",
    loading: "Loading…",
    close: "Close",
    bookIntro: "Long-route buses at the official fare. Choose your route and day.",
    travelDate: "Date of travel",
    noTrips: "No buses on this route that day. Try another date.",
    departs: "Departs {time}",
    seatsLeft: "{left} of {total} seats left",
    soldOut: "Sold out",
    permitVerified: "Permit verified",
    electric: "Electric",
    chooseSeat: "Choose seat",
    backToResults: "Back to results",
    missingStretch: "Choose a trip from the search first.",
    tripNotFound: "This trip isn't on sale.",
    stepSeat: "Choose a seat",
    stepSignIn: "Sign in",
    stepPay: "Pay",
    seatMap: "Seats",
    front: "Front",
    seatFree: "Free",
    seatTaken: "Taken",
    seatYours: "Your seat",
    seatAriaFree: "Seat {no}, free",
    seatAriaTaken: "Seat {no}, taken",
    seatAriaYours: "Seat {no}, held for you",
    seatJustTaken: "That seat was just taken. Choose another one.",
    holdExpired: "Your seat hold expired. Choose a seat again.",
    seatHeld: "Seat {seat} is held for you.",
    timeLeft: "Time left: {time}",
    chooseAnotherSeat: "Choose another seat",
    signedInAs: "Signed in as {phone}",
    sessionEnded: "You were signed out. Sign in again to pay.",
    paymentMethod: "Payment method",
    payWallet: "Wallet",
    payQr: "QR",
    payBank: "Bank",
    payCash: "Cash",
    paySimulated: "Simulated: no real money moves in this demo.",
    payAmount: "Pay {amount}",
    paying: "Paying…",
    signInIntro: "We'll text a 6-digit code to your mobile.",
    signInForTickets: "Sign in to see your tickets.",
    phoneLabel: "Mobile number",
    phoneHint: "98XXXXXXXX",
    phoneInvalid: "Enter a Nepali mobile number, like 98XXXXXXXX.",
    sendCode: "Send code",
    sendingCode: "Sending…",
    codeSent: "We sent a code to {phone}. It's valid for 5 minutes.",
    codeLabel: "6-digit code",
    codeInvalid: "Enter the 6 digits from the SMS.",
    codeWrong: "That code is wrong or has expired.",
    verifyCode: "Sign in",
    verifyingCode: "Checking…",
    resendCode: "Send a new code",
    changeNumber: "Change number",
    tooManyAttempts: "Too many tries. Wait a few minutes and try again.",
    noTickets: "You have no tickets yet.",
    seatNo: "Seat {no}",
    statusHeld: "On hold",
    statusConfirmed: "Confirmed",
    statusCancelled: "Cancelled",
    statusExpired: "Expired",
    statusReleased: "Released",
    ticketCode: "Ticket code",
    ticketNotFound: "We couldn't find this ticket.",
    route: "Route",
    departure: "Departure",
    seat: "Seat",
    operator: "Operator",
    chargedFare: "Charged fare",
    officialFareRespected: "Official fare respected",
    phone: "Phone",
    showQr: "Show this code to the conductor when you board.",
    qrAlt: "QR code for ticket {code}",
    cancelNowTerms: "If you cancel now, {percent}% of the fare is kept as a fee.",
    cancellationFee: "Cancellation fee",
    refund: "Refund",
    cancelTicket: "Cancel ticket",
    cancelConfirm: "Cancel this ticket? You get {refund} back.",
    cancelYes: "Yes, cancel it",
    cancelNo: "Keep my ticket",
    cancelling: "Cancelling…",
    ticketCancelled: "Ticket cancelled. {refund} will be refunded.",
    cannotCancel: "This ticket can no longer be cancelled.",
    verifyIntro: "Scan the QR sticker in the vehicle with your phone's camera, or type the code printed under it.",
    stickerCodeLabel: "Sticker code",
    stickerCodeHint: "8 letters and numbers, printed under the QR code.",
    stickerCodeInvalid: "Enter the 8 letters and numbers printed on the sticker.",
    checkVehicle: "Check vehicle",
    verifyChecking: "Checking the registry…",
    outcomeValid: "Valid",
    outcomeValidBody: "Route permit, insurance and fitness are all in order.",
    outcomeAttention: "Valid, expiring soon",
    outcomeAttentionBody: "Everything is valid today, but something expires within 30 days.",
    outcomeInvalid: "Do not board",
    outcomeInvalidBody: "Something has expired, or this vehicle has no valid route permit.",
    outcomeUnknown: "Status unknown",
    stickerNotFound: "Sticker not recognised",
    stickerNotFoundBody: "No vehicle in the registry has the code {code}. Check the code; if it matches the sticker, the sticker may be fake.",
    tryAgain: "Try again",
    plateNo: "Plate number",
    stickerNo: "Sticker {code}",
    insurance: "Insurance",
    fitness: "Fitness",
    routePermits: "Route permits",
    noPermits: "No route permit is on record for this vehicle.",
    otherStretchFare: "Check the fare for another stretch",
    reportThisVehicle: "Report a problem with this vehicle",
    checkedAt: "Checked {date} at {time}",
    verifyAnother: "Check another vehicle",
    validExpiredOn: "Expired on {date}",
    permitStarts: "Starts {date}",
    permitNotInForce: "Not in force",
    permitNo: "Permit {no}",
    permitFare: "Official fare, {from} to {to}",
    fareTableLabel: "Fare table: {label}",
    validUntil: "Valid until {date}",
    validExpiresToday: "Expires today",
    validExpiresTomorrow: "Expires tomorrow",
    validExpiresInDays: "Expires in {n} days",
    complaintsTitle: "Complaints",
    complaintsIntro: "Report overcharging, a refused ticket, unsafe driving and more, then follow it with your tracking number.",
    complaintsBody: "Overcharged or felt unsafe? File a complaint and track it.",
    reportProblem: "Make a complaint",
    trackComplaint: "Track a complaint",
    trackingNo: "Tracking number",
    trackingNoHint: "Like YC-AB12CD34",
    trackingNoInvalid: "Enter a tracking number like YC-AB12CD34.",
    track: "Track",
    complaintFiled: "Complaint filed. Keep this tracking number to follow it.",
    complaintNotFound: "We couldn't find a complaint with this tracking number.",
    complaintProgress: "Progress",
    stepDone: "done",
    complaintPendingHint: "This page shows the latest progress. Come back to it with your tracking number.",
    complaintCategory: "Category",
    filedOn: "Filed",
    resolvedOn: "Resolved",
    resolutionNote: "Response from the authority",
    complaintStatusOpen: "Received",
    complaintStatusAssigned: "Being looked into",
    complaintStatusResolved: "Resolved",
    complaintCatOvercharging: "Overcharging",
    complaintCatRefusedTicket: "Refused a ticket",
    complaintCatUnsafeDriving: "Unsafe driving",
    complaintCatHarassment: "Harassment",
    complaintCatVehicleCondition: "Vehicle condition",
    complaintCatDelay: "Delay or cancellation",
    complaintCatOther: "Other",
    complaintIntro: "Your complaint goes to the transport authority. You don't need to sign in or give your name.",
    complaintWhatHappened: "What happened?",
    chooseCategory: "Choose what happened.",
    complaintAbout: "What it's about",
    aboutSticker: "Vehicle sticker",
    aboutTrip: "Trip",
    changeAbout: "Change",
    aboutHint: "Enter the vehicle's sticker code, your ticket code, or both.",
    ticketCodeHint: "Like YT-ABC123",
    ticketCodeInvalid: "Enter a ticket code like YT-ABC123.",
    needAbout: "Enter a sticker code or a ticket code.",
    complaintDescription: "Describe what happened",
    complaintDescriptionHint: "When and where, and what was said or charged.",
    descriptionTooShort: "Write at least 10 characters.",
    charCount: "{n}/{max}",
    locationLabel: "Where it happened (optional)",
    shareLocation: "Share my location",
    locating: "Finding your location…",
    locationAttached: "Location attached, accurate to about {m} m",
    removeLocation: "Remove location",
    locationHint: "Sent only if you tap the button. It helps inspectors find where this happened.",
    locationInsecure: "Location only works over a secure (https) connection. You can send the complaint without it.",
    locationUnavailable: "Couldn't find your location. You can send the complaint without it.",
    locationDenied: "Location access was denied. You can send the complaint without it.",
    locationOutsideNepal: "That location is outside Nepal, so it wasn't attached.",
    photoLabel: "Photo (optional)",
    addPhoto: "Take or add a photo",
    photoPreparing: "Preparing the photo…",
    photoAlt: "The photo to attach",
    removePhoto: "Remove photo",
    photoHint: "A photo of the fare charged, a receipt or the vehicle helps. It's made smaller before sending.",
    photoTooLarge: "This photo is too large even after shrinking it. Choose another one.",
    photoUnreadable: "Couldn't open this photo. Choose another one.",
    complaintInvalid: "Some details weren't accepted. Check the form and try again.",
    complaintRefNotFound: "We couldn't find that vehicle, ticket or trip. Check the code.",
    complaintTooMany: "Too many complaints from here just now. Please try again later.",
    sendComplaint: "Send complaint",
    sendingComplaint: "Sending…",
    smsButton: "SMS (demo)",
    smsTitle: "Text messages sent (demo)",
    smsEmpty: "No messages yet.",
    smsUnread: "new messages",
    smsNewTag: "New",
    smsCopyCode: "Copy code {code}",
    smsCopied: "Copied",
    consoleNav: "Planning and compliance",
    consoleAuthorityDotm: "Department of Transport Management",
    consoleStateScheduled: "Scheduled",
    consoleStateBoarding: "Boarding",
    consoleStateDeparted: "Departed",
    consoleStateLate: "Late",
    consoleStateCancelled: "Cancelled",
    seatSelected: "Selected",
    seatAriaSelected: "Seat {no}, selected",
    navOperator: "Operators and counters",
    roleStaff: "Staff",
    roleOperatorAdmin: "Operator admin",
    roleCounterClerk: "Counter clerk",
    roleConductor: "Conductor",
    roleRegulatorAdmin: "Regulator admin",
    rolePlanner: "Planner",
    roleInspector: "Inspector",
    tripDeparted: "Departed",
    tripCancelled: "Cancelled",
    channelWeb: "Web",
    channelCounter: "Counter",
    channelPartner: "Partner",
    ticket: "Ticket",
    daySun: "Sun",
    dayMon: "Mon",
    dayTue: "Tue",
    dayWed: "Wed",
    dayThu: "Thu",
    dayFri: "Fri",
    daySat: "Sat",
    swapCurrent: "On this trip now",
    swapNoPermit: "No route permit for this route on this day",
    swapClass: "Not the class this trip was sold as",
    swapSeats: "Lacks seats already sold",
    docRoutePermit: "Route permit",
    docInsurance: "Insurance",
    docFitness: "Fitness",
    safetyTitle: "Trip safety",
    safetyDemoTag: "Demo — nothing is sent",
    safetyShare: "Share trip",
    safetySos: "SOS",
    safetyShareTitle: "Share trip (demo)",
    safetyShareBody: "In the real service this sends a link to a contact you choose, so they can follow the bus live until you arrive. The link stops working once the trip ends.",
    safetyShareSends: "Your contact would see:",
    safetySosTitle: "SOS (demo)",
    safetySosBody: "In the real service this alerts the operator's duty desk and Nepal Police (100) with your trip, the bus plate and your phone's location, and they call you back.",
    safetySosSends: "The alert would carry:",
    safetyNothingSent: "This is a demo: nothing has been sent, shared or dialled. In an emergency, call 100.",
    safetyClose: "Close",
  },
  ne: {
    appName: "सार्वजनिक यातायात सेवा",
    siteNameNe: "सार्वजनिक यातायात सेवा",
    siteNameEn: "Public Transport Services",
    proofOfConcept: "अवधारणा परीक्षण",
    systemName: "एकीकृत सार्वजनिक यातायात सूचना प्रणाली (IPTIS), प्रस्तावित नाम",
    footerLabel: "साइटका लिंक",
    footerPassengers: "यात्रु",
    footerOperators: "सञ्चालक",
    footerGovernment: "सरकार",
    footerPartners: "साझेदार तथा विकासकर्ता",
    footerDemoWallet: "डेमो वालेट",
    footerNotice: "अवधारणा परीक्षण · डेमो डाटा · सरकारी सेवा होइन",
    partnerApi: "साझेदार API",
    tagline: "आधिकारिक भाडा, भरपर्दो तालिका, एकै ठाउँमा।",
    demoData: "डेमो डाटा",
    switchLanguage: "English",
    fareCheck: "आधिकारिक भाडा हेर्नुहोस्",
    fareCheckBody: "तिर्नुअघि कुनै पनि रुटको कानुनी भाडा हेर्नुहोस्।",
    bookSeat: "बस टिकट लिनुहोस्",
    bookSeatBody: "आधिकारिक भाडामा लामो दूरीका बस, जुनसुकै माध्यमबाट।",
    verifyVehicle: "सवारी जाँच गर्नुहोस्",
    verifyVehicleBody: "स्टिकर स्क्यान गरी रुट इजाजत, बीमा र भाडा जाँच्नुहोस्।",
    apiConnected: "प्रणालीसँग जोडिएको",
    apiPlaces: "{n} स्थान",
    apiDown: "प्रणालीसँग जोडिन सकिएन",
    notFound: "पृष्ठ भेटिएन",
    dateAd: "ई.सं. {date}",
    open: "खोल्नुहोस्",
    fareCheckIntro: "आज लागू यातायात व्यवस्था विभागको भाडादर तालिका अनुसार बस यात्राको कानुनी भाडा।",
    openData: "खुला डाटा",
    openDataLink: "खुला डाटा: रुट, बिसौनी, समय तालिका र भाडा, निःशुल्क पुनः प्रयोगका लागि",
    openDataIntro: "रुट, बिसौनी, समय तालिका र आधिकारिक भाडा निःशुल्क डाउनलोड र पुनः प्रयोग गर्नुहोस्। कुनै व्यक्तिगत विवरण प्रकाशित गरिँदैन।",
    openDataGtfs: "GTFS फिड",
    openDataGtfsBody: "यात्रा योजना र नक्सा एपले पढ्ने मानक ढाँचा। समय नेपाली समयमा; प्रस्थानपछिका समय अनुमानित हुन्।",
    openDataRoutes: "रुट",
    openDataRoutesBody: "हरेक इजाजत प्राप्त रुट, क्रमैसँग बिसौनी र दूरीसहित।",
    openDataStops: "बिसौनी",
    openDataStopsBody: "बसपार्क, बिसौनी र सहर, स्थानसहित।",
    openDataSchedules: "समय तालिका",
    openDataSchedulesBody: "रुट, बसको किसिम र बार अनुसार हरेक सञ्चालकका नियमित प्रस्थान।",
    openDataFares: "आधिकारिक भाडा",
    openDataFaresBody: "लागू भाडादर तालिका अनुसार रुटका कुनै पनि दुई बिसौनीबीच हरेक किसिमको बसको कानुनी भाडा।",
    openDataDownload: "डाउनलोड",
    openDataLicence: "सार्वजनिक यातायात सेवालाई श्रेय दिएर निःशुल्क पुनः प्रयोग गर्न सकिन्छ। बिसौनीका स्थान अनुमानित हुन्; सञ्चालक र समय तालिका डेमो डाटा हुन्।",
    installApp: "एप इन्स्टल गर्नुहोस्",
    installLink: "फोनमा इन्स्टल गरी एपजस्तै खोल्नुहोस्",
    from: "कहाँबाट",
    to: "कहाँसम्म",
    choosePlace: "ठाउँ छान्नुहोस्",
    busClass: "बसको किसिम",
    classOrdinary: "साधारण",
    classDeluxe: "डिलक्स",
    classSuperDeluxe: "सुपर डिलक्स",
    electricBus: "विद्युतीय बस",
    samePlace: "फरक दुई ठाउँ छान्नुहोस्।",
    noRoute: "यी ठाउँबीच कुनै रुट चल्दैन।",
    noFareTable: "यो रुटमा कुनै भाडादर तालिका लागू छैन।",
    somethingWrong: "केही गडबड भयो। फेरि प्रयास गर्नुहोस्।",
    officialFare: "आधिकारिक भाडा",
    distanceKm: "{km} कि.मि.",
    fareTable: "भाडादर तालिका: {label}, {date} देखि लागू",
    howWorkedOut: "यो भाडा कसरी निकालियो",
    howWorkedOutNote: "हरेक खण्डमा त्यस सडकको प्रति कि.मि. दर लाग्छ; बसको किसिम अनुसारको गुणक कुल रकममा लाग्छ र नजिकको रुपैयाँमा मिलाइन्छ।",
    stretch: "खण्ड",
    road: "सडक",
    km: "कि.मि.",
    perKm: "प्रति कि.मि.",
    roadTerai: "तराई",
    roadHill: "पहाडी",
    roadKtmPokhara: "काठमाडौं–पोखरा खण्ड",
    mainNav: "मुख्य मेनु",
    navBook: "बुकिङ",
    navTickets: "मेरा टिकट",
    signOut: "साइन आउट",
    loading: "लोड हुँदैछ…",
    close: "बन्द गर्नुहोस्",
    bookIntro: "आधिकारिक भाडामा लामो दूरीका बस। आफ्नो रुट र दिन छान्नुहोस्।",
    travelDate: "यात्रा मिति",
    noTrips: "त्यो दिन यो रुटमा बस छैन। अर्को मिति छानेर हेर्नुहोस्।",
    departs: "{time} बजे छुट्छ",
    seatsLeft: "{total} मध्ये {left} सिट बाँकी",
    soldOut: "सबै सिट बिक्यो",
    permitVerified: "रुट इजाजत प्रमाणित",
    electric: "विद्युतीय",
    chooseSeat: "सिट छान्नुहोस्",
    backToResults: "खोज नतिजामा फर्कनुहोस्",
    missingStretch: "पहिले खोजबाट यात्रा छान्नुहोस्।",
    tripNotFound: "यो यात्राको टिकट बिक्रीमा छैन।",
    stepSeat: "सिट छान्नुहोस्",
    stepSignIn: "साइन इन",
    stepPay: "भुक्तानी",
    seatMap: "सिटहरू",
    front: "अगाडि",
    seatFree: "खाली",
    seatTaken: "बुक भइसकेको",
    seatYours: "तपाईंको सिट",
    seatAriaFree: "सिट {no}, खाली",
    seatAriaTaken: "सिट {no}, बुक भइसकेको",
    seatAriaYours: "सिट {no}, तपाईंका लागि रोकिएको",
    seatJustTaken: "त्यो सिट भर्खरै अरू कसैले लियो। अर्को सिट छान्नुहोस्।",
    holdExpired: "सिट रोक्ने समय सकियो। फेरि सिट छान्नुहोस्।",
    seatHeld: "सिट {seat} तपाईंका लागि रोकिएको छ।",
    timeLeft: "बाँकी समय: {time}",
    chooseAnotherSeat: "अर्को सिट छान्नुहोस्",
    signedInAs: "{phone} बाट साइन इन गरिएको",
    sessionEnded: "तपाईं साइन आउट हुनुभयो। भुक्तानी गर्न फेरि साइन इन गर्नुहोस्।",
    paymentMethod: "भुक्तानी विधि",
    payWallet: "वालेट",
    payQr: "QR",
    payBank: "बैंक",
    payCash: "नगद",
    paySimulated: "डेमो भुक्तानी: यसमा साँच्चिकै पैसा कट्दैन।",
    payAmount: "{amount} तिर्नुहोस्",
    paying: "भुक्तानी हुँदैछ…",
    signInIntro: "तपाईंको मोबाइलमा ६ अङ्कको कोड SMS गरिनेछ।",
    signInForTickets: "आफ्ना टिकट हेर्न साइन इन गर्नुहोस्।",
    phoneLabel: "मोबाइल नम्बर",
    phoneHint: "९८XXXXXXXX",
    phoneInvalid: "९८XXXXXXXX जस्तो नेपाली मोबाइल नम्बर लेख्नुहोस्।",
    sendCode: "कोड पठाउनुहोस्",
    sendingCode: "पठाउँदै…",
    codeSent: "{phone} मा कोड पठाइयो। यो ५ मिनेटसम्म मान्य हुन्छ।",
    codeLabel: "६ अङ्कको कोड",
    codeInvalid: "SMS मा आएका ६ अङ्क लेख्नुहोस्।",
    codeWrong: "कोड मिलेन वा यसको समय सकियो।",
    verifyCode: "साइन इन गर्नुहोस्",
    verifyingCode: "जाँच्दै…",
    resendCode: "नयाँ कोड पठाउनुहोस्",
    changeNumber: "नम्बर बदल्नुहोस्",
    tooManyAttempts: "धेरै पटक प्रयास भयो। केही मिनेटपछि फेरि प्रयास गर्नुहोस्।",
    noTickets: "तपाईंसँग अहिलेसम्म कुनै टिकट छैन।",
    seatNo: "सिट {no}",
    statusHeld: "रोकिएको",
    statusConfirmed: "पक्का भएको",
    statusCancelled: "रद्द गरिएको",
    statusExpired: "समय सकिएको",
    statusReleased: "छाडिएको",
    ticketCode: "टिकट कोड",
    ticketNotFound: "यो टिकट भेटिएन।",
    route: "रुट",
    departure: "प्रस्थान",
    seat: "सिट",
    operator: "सञ्चालक",
    chargedFare: "लिइएको भाडा",
    officialFareRespected: "आधिकारिक भाडा अनुसार",
    phone: "फोन",
    showQr: "बस चढ्दा यो कोड कन्डक्टरलाई देखाउनुहोस्।",
    qrAlt: "टिकट {code} को QR कोड",
    cancelNowTerms: "अहिले रद्द गर्दा भाडाको {percent}% शुल्क काटिन्छ।",
    cancellationFee: "रद्द शुल्क",
    refund: "फिर्ता रकम",
    cancelTicket: "टिकट रद्द गर्नुहोस्",
    cancelConfirm: "यो टिकट रद्द गर्ने? तपाईंले {refund} फिर्ता पाउनुहुनेछ।",
    cancelYes: "हो, रद्द गर्नुहोस्",
    cancelNo: "टिकट राख्नुहोस्",
    cancelling: "रद्द गर्दै…",
    ticketCancelled: "टिकट रद्द भयो। {refund} फिर्ता हुनेछ।",
    cannotCancel: "यो टिकट अब रद्द गर्न मिल्दैन।",
    verifyIntro: "सवारीभित्र टाँसिएको QR स्टिकर फोनको क्यामेराले स्क्यान गर्नुहोस्, वा त्यसमुनि छापिएको कोड लेख्नुहोस्।",
    stickerCodeLabel: "स्टिकर कोड",
    stickerCodeHint: "QR कोडमुनि छापिएका ८ अक्षर र अङ्क।",
    stickerCodeInvalid: "स्टिकरमा छापिएका ८ अक्षर र अङ्क लेख्नुहोस्।",
    checkVehicle: "सवारी जाँच्नुहोस्",
    verifyChecking: "दर्ता अभिलेख जाँच्दै…",
    outcomeValid: "मान्य",
    outcomeValidBody: "रुट इजाजत, बीमा र जाँचपास सबै ठीक छन्।",
    outcomeAttention: "मान्य, तर म्याद सकिन लागेको",
    outcomeAttentionBody: "आज सबै मान्य छ, तर ३० दिनभित्र कुनैको म्याद सकिँदैछ।",
    outcomeInvalid: "यो सवारी नचढ्नुहोस्",
    outcomeInvalidBody: "कुनै कागजातको म्याद सकिएको छ, वा यो सवारीसँग मान्य रुट इजाजत छैन।",
    outcomeUnknown: "अवस्था थाहा भएन",
    stickerNotFound: "स्टिकर चिनिएन",
    stickerNotFoundBody: "दर्ता अभिलेखमा {code} कोड भएको कुनै सवारी छैन। कोड जाँच्नुहोस्; स्टिकरसँग मिल्छ भने स्टिकर नक्कली हुन सक्छ।",
    tryAgain: "फेरि प्रयास गर्नुहोस्",
    plateNo: "नम्बर प्लेट",
    stickerNo: "स्टिकर {code}",
    insurance: "बीमा",
    fitness: "जाँचपास",
    routePermits: "रुट इजाजत",
    noPermits: "यो सवारीको कुनै रुट इजाजत अभिलेखमा छैन।",
    otherStretchFare: "अर्को खण्डको भाडा हेर्नुहोस्",
    reportThisVehicle: "यो सवारीबारे उजुरी गर्नुहोस्",
    checkedAt: "{date}, {time} मा जाँच गरिएको",
    verifyAnother: "अर्को सवारी जाँच्नुहोस्",
    validExpiredOn: "{date} मा म्याद सकियो",
    permitStarts: "{date} देखि लागू हुने",
    permitNotInForce: "लागू छैन",
    permitNo: "इजाजत नं. {no}",
    permitFare: "आधिकारिक भाडा, {from} देखि {to} सम्म",
    fareTableLabel: "भाडादर तालिका: {label}",
    validUntil: "{date} सम्म मान्य",
    validExpiresToday: "आज म्याद सकिन्छ",
    validExpiresTomorrow: "भोलि म्याद सकिन्छ",
    validExpiresInDays: "{n} दिनमा म्याद सकिन्छ",
    complaintsTitle: "उजुरी",
    complaintsIntro: "बढी भाडा, टिकट नदिएको, असुरक्षित ड्राइभिङ लगायतका उजुरी गर्नुहोस्, अनि ट्र्याकिङ नम्बरबाट त्यसको अवस्था हेर्नुहोस्।",
    complaintsBody: "बढी भाडा लियो वा असुरक्षित लाग्यो? उजुरी गरेर त्यसको अवस्था हेर्नुहोस्।",
    reportProblem: "उजुरी दर्ता गर्नुहोस्",
    trackComplaint: "उजुरीको अवस्था हेर्नुहोस्",
    trackingNo: "ट्र्याकिङ नम्बर",
    trackingNoHint: "जस्तै YC-AB12CD34",
    trackingNoInvalid: "YC-AB12CD34 जस्तो ट्र्याकिङ नम्बर लेख्नुहोस्।",
    track: "हेर्नुहोस्",
    complaintFiled: "उजुरी दर्ता भयो। अवस्था हेर्न यो ट्र्याकिङ नम्बर राख्नुहोस्।",
    complaintNotFound: "यो ट्र्याकिङ नम्बरको कुनै उजुरी भेटिएन।",
    complaintProgress: "प्रगति",
    stepDone: "पूरा भयो",
    complaintPendingHint: "यो पृष्ठमा पछिल्लो अवस्था देखिन्छ। ट्र्याकिङ नम्बरबाट फेरि हेर्न सक्नुहुन्छ।",
    complaintCategory: "किसिम",
    filedOn: "दर्ता मिति",
    resolvedOn: "समाधान मिति",
    resolutionNote: "निकायको जवाफ",
    complaintStatusOpen: "दर्ता भयो",
    complaintStatusAssigned: "छानबिन हुँदैछ",
    complaintStatusResolved: "समाधान भयो",
    complaintCatOvercharging: "बढी भाडा",
    complaintCatRefusedTicket: "टिकट नदिएको",
    complaintCatUnsafeDriving: "असुरक्षित ड्राइभिङ",
    complaintCatHarassment: "दुर्व्यवहार",
    complaintCatVehicleCondition: "सवारीको अवस्था",
    complaintCatDelay: "ढिलाइ वा रद्द",
    complaintCatOther: "अन्य",
    complaintIntro: "तपाईंको उजुरी यातायात कार्यालयमा पुग्छ। साइन इन गर्न वा नाम दिन पर्दैन।",
    complaintWhatHappened: "के भयो?",
    chooseCategory: "के भयो, छान्नुहोस्।",
    complaintAbout: "उजुरी केबारे हो",
    aboutSticker: "सवारी स्टिकर",
    aboutTrip: "यात्रा",
    changeAbout: "बदल्नुहोस्",
    aboutHint: "सवारीको स्टिकर कोड, तपाईंको टिकट कोड, वा दुवै लेख्नुहोस्।",
    ticketCodeHint: "जस्तै YT-ABC123",
    ticketCodeInvalid: "YT-ABC123 जस्तो टिकट कोड लेख्नुहोस्।",
    needAbout: "स्टिकर कोड वा टिकट कोड लेख्नुहोस्।",
    complaintDescription: "के भयो, लेख्नुहोस्",
    complaintDescriptionHint: "कहिले र कहाँ भयो, के भनियो वा कति भाडा लिइयो।",
    descriptionTooShort: "कम्तीमा १० अक्षर लेख्नुहोस्।",
    charCount: "{n}/{max}",
    locationLabel: "घटना भएको ठाउँ (ऐच्छिक)",
    shareLocation: "मेरो स्थान पठाउनुहोस्",
    locating: "तपाईंको स्थान पत्ता लगाउँदै…",
    locationAttached: "स्थान थपियो, करिब {m} मिटरसम्म सही",
    removeLocation: "स्थान हटाउनुहोस्",
    locationHint: "बटन थिच्नुभयो भने मात्र पठाइन्छ। घटना कहाँ भयो भनी निरीक्षकलाई पत्ता लगाउन सजिलो हुन्छ।",
    locationInsecure: "स्थान सुरक्षित (https) जडानमा मात्र चल्छ। स्थान बिना पनि उजुरी पठाउन सकिन्छ।",
    locationUnavailable: "तपाईंको स्थान पत्ता लागेन। स्थान बिना पनि उजुरी पठाउन सकिन्छ।",
    locationDenied: "स्थान हेर्ने अनुमति दिइएन। स्थान बिना पनि उजुरी पठाउन सकिन्छ।",
    locationOutsideNepal: "त्यो स्थान नेपालबाहिर देखियो, त्यसैले थपिएन।",
    photoLabel: "फोटो (ऐच्छिक)",
    addPhoto: "फोटो खिच्नुहोस् वा थप्नुहोस्",
    photoPreparing: "फोटो तयार हुँदैछ…",
    photoAlt: "थपिने फोटो",
    removePhoto: "फोटो हटाउनुहोस्",
    photoHint: "लिइएको भाडा, रसिद वा सवारीको फोटोले मद्दत गर्छ। पठाउनुअघि फोटो सानो बनाइन्छ।",
    photoTooLarge: "सानो बनाउँदा पनि यो फोटो धेरै ठूलो भयो। अर्को फोटो छान्नुहोस्।",
    photoUnreadable: "यो फोटो खोल्न सकिएन। अर्को फोटो छान्नुहोस्।",
    complaintInvalid: "केही विवरण स्वीकार भएनन्। फाराम जाँचेर फेरि प्रयास गर्नुहोस्।",
    complaintRefNotFound: "त्यो सवारी, टिकट वा यात्रा भेटिएन। कोड जाँच्नुहोस्।",
    complaintTooMany: "अहिले यहाँबाट धेरै उजुरी परिसके। केही बेरपछि फेरि प्रयास गर्नुहोस्।",
    sendComplaint: "उजुरी पठाउनुहोस्",
    sendingComplaint: "पठाउँदै…",
    smsButton: "SMS (डेमो)",
    smsTitle: "पठाइएका SMS (डेमो)",
    smsEmpty: "अहिलेसम्म कुनै सन्देश छैन।",
    smsUnread: "नयाँ सन्देश",
    smsNewTag: "नयाँ",
    smsCopyCode: "कोड {code} कपी गर्नुहोस्",
    smsCopied: "कपी भयो",
    consoleNav: "योजना तथा अनुपालन",
    consoleAuthorityDotm: "यातायात व्यवस्था विभाग",
    consoleStateScheduled: "तालिकामा",
    consoleStateBoarding: "चढ्दै",
    consoleStateDeparted: "प्रस्थान भयो",
    consoleStateLate: "ढिला",
    consoleStateCancelled: "रद्द",
    seatSelected: "छानिएको",
    seatAriaSelected: "सिट {no}, छानिएको",
    navOperator: "सञ्चालक तथा काउन्टर",
    roleStaff: "कर्मचारी",
    roleOperatorAdmin: "सञ्चालक प्रशासक",
    roleCounterClerk: "काउन्टर कर्मचारी",
    roleConductor: "कन्डक्टर",
    roleRegulatorAdmin: "नियामक प्रशासक",
    rolePlanner: "योजनाकार",
    roleInspector: "निरीक्षक",
    tripDeparted: "प्रस्थान भइसक्यो",
    tripCancelled: "रद्द भयो",
    channelWeb: "वेब",
    channelCounter: "काउन्टर",
    channelPartner: "साझेदार",
    ticket: "टिकट",
    daySun: "आइत",
    dayMon: "सोम",
    dayTue: "मंगल",
    dayWed: "बुध",
    dayThu: "बिही",
    dayFri: "शुक्र",
    daySat: "शनि",
    swapCurrent: "अहिले यही यात्रामा",
    swapNoPermit: "यस दिन यो रुटको इजाजत छैन",
    swapClass: "यात्रा बेचिएको किसिमको होइन",
    swapSeats: "बिक्री भइसकेका सिट छैनन्",
    docRoutePermit: "रुट इजाजत",
    docInsurance: "बीमा",
    docFitness: "जाँचपास",
    safetyTitle: "यात्रा सुरक्षा",
    safetyDemoTag: "डेमो — केही पनि पठाइँदैन",
    safetyShare: "यात्रा सेयर गर्नुहोस्",
    safetySos: "SOS",
    safetyShareTitle: "यात्रा सेयर (डेमो)",
    safetyShareBody: "वास्तविक सेवामा यसले तपाईंले रोजेको सम्पर्कलाई लिंक पठाउँछ, जसबाट उहाँले तपाईं नपुगुन्जेल बसलाई लाइभ हेर्न सक्नुहुन्छ। यात्रा सकिएपछि लिंक चल्दैन।",
    safetyShareSends: "तपाईंको सम्पर्कले यो देख्नुहुनेछ:",
    safetySosTitle: "SOS (डेमो)",
    safetySosBody: "वास्तविक सेवामा यसले सञ्चालकको ड्युटी डेस्क र नेपाल प्रहरी (१००) लाई तपाईंको यात्रा, बसको नम्बर प्लेट र फोनको स्थानसहित सूचना पठाउँछ, र उहाँहरूले तपाईंलाई फोन गर्नुहुन्छ।",
    safetySosSends: "सूचनामा यी कुरा जान्थे:",
    safetyNothingSent: "यो डेमो हो: केही पनि पठाइएको, सेयर गरिएको वा फोन गरिएको छैन। आपत्कालमा १०० मा फोन गर्नुहोस्।",
    safetyClose: "बन्द गर्नुहोस्",
  },
});

export type MessageKey = keyof (typeof messages)["en"] | keyof (typeof staffMessages)["en"];

/** Returns the table unchanged; it exists so a key missing from either language fails to compile. */
export function bothLanguages<T extends Record<string, string>>(table: { en: T; ne: Record<keyof NoInfer<T>, string> }) {
  return table;
}

// What t reads. The staff strings join it once loadStaffMessages resolves.
const tables: Record<Lang, Record<string, string>> = { en: { ...messages.en }, ne: { ...messages.ne } };
let staffLoad: Promise<void> | undefined;

/** Fetches the operator app's and the console's strings; their layout routes wait for it. */
export function loadStaffMessages(): Promise<void> {
  staffLoad ??= import("~/lib/i18n-staff").then(
    ({ staffMessages }) => {
      Object.assign(tables.en, staffMessages.en);
      Object.assign(tables.ne, staffMessages.ne);
    },
    (err: unknown) => {
      // Let the next navigation try again, say once the signal is back.
      staffLoad = undefined;
      throw err;
    },
  );
  return staffLoad;
}

type I18n = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18n | null>(null);
const storageKey = "yatayat.lang";

// Storage can throw in private windows or when site data is blocked; Nepali is the default.
function initialLang(): Lang {
  try {
    return localStorage.getItem(storageKey) === "en" ? "en" : "ne";
  } catch {
    return "ne";
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      // Not remembering the choice is fine.
    }
  }, []);

  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang,
      t: (key, vars) =>
        (tables[lang][key] ?? key).replace(/\{(\w+)\}/g, (_, name: string) => String(vars?.[name] ?? `{${name}}`)),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n needs an I18nProvider");
  return ctx;
}
